// src/components/LearningItemCard.jsx
import React from "react";
import { FaStar, FaRegStar } from "react-icons/fa";
import { motion } from "framer-motion";

/** Small helper: get YouTube video id from url */
function getYouTubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) {
      // https://youtu.be/VIDEOID
      return u.pathname.slice(1);
    }
    // https://www.youtube.com/watch?v=VIDEOID
    const v = u.searchParams.get("v");
    if (v) return v;

    // Fallback: last part of path
    const parts = u.pathname.split("/");
    return parts[parts.length - 1];
  } catch {
    return null;
  }
}

/**
 * Props:
 *  - item: { id, title, description, text, video, audio, pdf? }
 *  - isFav: boolean
 *  - toggleFav: function(itemId)
 *  - customContent?: ReactNode (optional extra section shown above Text)
 */
export default function LearningItemCard({
  item,
  isFav,
  toggleFav,
  customContent,
}) {
  const ytId =
    item.video && (item.video.includes("youtube.com") || item.video.includes("youtu.be"))
      ? getYouTubeId(item.video)
      : null;

  const isFileVideo =
    item.video &&
    (item.video.endsWith(".mp4") || item.video.endsWith(".webm") || item.video.endsWith(".ogg"));

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-5 rounded-2xl shadow border"
    >
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="pr-4">
          <h2 className="text-xl font-semibold">{item.title}</h2>
          {item.description && (
            <p className="text-gray-600 text-sm mt-1">{item.description}</p>
          )}
        </div>

        <button
          onClick={() => toggleFav(item.id)}
          className="text-yellow-400 text-2xl p-1"
          aria-label={`Toggle favorite ${item.title}`}
        >
          {isFav ? <FaStar /> : <FaRegStar />}
        </button>
      </div>

      {/* Optional custom section (sliders, etc.) */}
      {customContent && <div className="mt-4">{customContent}</div>}

      {/* Text – short colourful paragraph, with a tiny hover animation */}
      {item.text && (
        <motion.div
          className="mt-4"
          whileHover={{ scale: 1.01 }}
          transition={{ type: "spring", stiffness: 200, damping: 15 }}
        >
          <h3 className="font-semibold text-sm mb-1">Text</h3>
          <div className="bg-gradient-to-r from-green-50 via-yellow-50 to-pink-50 p-3 rounded text-sm text-gray-800 leading-relaxed">
            {item.text}
          </div>
        </motion.div>
      )}

      {/* Video */}
      {item.video && (
        <div className="mt-4">
          <h3 className="font-semibold text-sm mb-1">Video</h3>

          {/* YouTube embed */}
          {ytId && (
            <div className="relative w-full pb-[56.25%] rounded overflow-hidden shadow">
              <iframe
                src={`https://www.youtube.com/embed/${ytId}`}
                title={item.title}
                className="absolute top-0 left-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {/* Normal video file */}
          {!ytId && isFileVideo && (
            <video controls className="w-full rounded border">
              <source src={item.video} />
            </video>
          )}

          {/* Fallback: just a link */}
          {!ytId && !isFileVideo && (
            <a
              href={item.video}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-sm text-blue-600 underline"
            >
              Open video
            </a>
          )}
        </div>
      )}

      {/* Audio */}
      {item.audio && (
        <div className="mt-4">
          <h3 className="font-semibold text-sm mb-1">Audio</h3>
          <audio controls className="w-full">
            <source src={item.audio} />
          </audio>
        </div>
      )}

      {/* Optional PDF link */}
      {item.pdf && (
        <div className="mt-4">
          <a
            href={item.pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm px-3 py-1 bg-gray-100 rounded"
          >
            View PDF
          </a>
        </div>
      )}
    </motion.div>
  );
}




